import { useCanAccess } from "ra-core";
import { TagsList } from "./TagsList";
import { CreateTagButton } from "../tags/CreateTagButton";
import { Plus } from "lucide-react";

import {
  type Identifier,
  useGetList,
  useGetMany,
  useRecordContext,
  useUpdate,
} from "ra-core";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { TagChip } from "../tags/TagChip";

import type { Contact, Tag } from "../types";

const CONTACT_TAG_GROUPS = [
  {
    key: "clients",
    title: "Клиенты и проектирование",
    names: ["Заказчик", "Дизайнер", "Архитектор"],
  },

  {
    key: "works",
    title: "Работы и подрядчики",
    names: [
      "Подрядчик",
      "Монтаж",
      "Доставка",
      "Логистика",
      "Замер",
      "Производство",
      "Столяр",
      "Камень",
      "Стекло",
      "Металл",
    ],
  },

  {
    key: "suppliers",
    title: "Поставщики",
    names: [
      "Поставщик",
      "Фурнитура",
      "Материалы",
      "Оборудование",
      "Менеджер магазина",
    ],
  },
] as const;

const normalizeTagName = (value?: string | null) =>
  (value ?? "").trim().toLocaleLowerCase("ru-RU");

export const TagsListEdit = () => {
  const record = useRecordContext<Contact>();

  const { data: allTags = [], isPending: isPendingAllTags } = useGetList<Tag>(
    "tags",
    {
      pagination: {
        page: 1,
        perPage: 100,
      },

      sort: {
        field: "name",
        order: "ASC",
      },
    },
  );

  const selectedIds = record?.tags ?? [];

  const { data: selectedTags = [], isPending: isPendingSelectedTags } =
    useGetMany<Tag>(
      "tags",
      {
        ids: selectedIds,
      },
      {
        enabled: selectedIds.length > 0,
      },
    );

  const [update] = useUpdate<Contact>();
  const access = useCanAccess({ resource: "contacts", action: "edit" });
  if (!access.canAccess) return <TagsList />;

  if (!record) {
    return null;
  }

  const handleTagAdd = (id: Identifier) => {
    if (selectedIds.some((tagId) => String(tagId) === String(id))) {
      return;
    }

    update(
      "contacts",
      {
        id: record.id,

        data: {
          tags: [...selectedIds, Number(id)],
        },

        previousData: record,
      },
      {
        mutationMode: "optimistic",
      },
    );
  };

  const handleTagDelete = (id: Identifier) => {
    update(
      "contacts",
      {
        id: record.id,

        data: {
          tags: selectedIds.filter((tagId) => String(tagId) !== String(id)),
        },

        previousData: record,
      },
      {
        mutationMode: "optimistic",
      },
    );
  };

  if (isPendingAllTags || isPendingSelectedTags) {
    return null;
  }

  const unselectedTags = allTags.filter(
    (tag) => !selectedIds.some((tagId) => String(tagId) === String(tag.id)),
  );

  const groupedNames = new Set(
    CONTACT_TAG_GROUPS.flatMap((group) => group.names.map(normalizeTagName)),
  );

  const otherTags = unselectedTags.filter(
    (tag) => !groupedNames.has(normalizeTagName(tag.name)),
  );

  const hasAvailableTags = unselectedTags.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <CreateTagButton onCreated={(tag) => handleTagAdd(tag.id)} />
      {selectedTags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onUnlink={() => handleTagDelete(tag.id)}
        />
      ))}

      {hasAvailableTags && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 md:h-7 cursor-pointer"
            >
              <Plus className="w-4 h-4 md:w-3 md:h-3 mr-1" />
              Добавить
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            className="w-64 max-h-[420px] overflow-y-auto"
          >
            {CONTACT_TAG_GROUPS.map((group) => {
              const groupTags = unselectedTags.filter((tag) =>
                group.names.some(
                  (name) =>
                    normalizeTagName(name) === normalizeTagName(tag.name),
                ),
              );

              if (groupTags.length === 0) {
                return null;
              }

              return (
                <div key={group.key} className="py-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {group.title}
                  </div>

                  {groupTags.map((tag) => (
                    <DropdownMenuItem
                      key={tag.id}
                      onSelect={() => handleTagAdd(tag.id)}
                    >
                      <Badge
                        variant="secondary"
                        className="font-normal rovero-colored-tag text-[#482719]"
                        style={{
                          backgroundColor: tag.color,
                        }}
                      >
                        {tag.name}
                      </Badge>
                    </DropdownMenuItem>
                  ))}
                </div>
              );
            })}

            {otherTags.length > 0 && (
              <div className="py-1 border-t">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Другие
                </div>

                {otherTags.map((tag) => (
                  <DropdownMenuItem
                    key={tag.id}
                    onSelect={() => handleTagAdd(tag.id)}
                  >
                    <Badge
                      variant="secondary"
                      className="font-normal rovero-colored-tag text-[#482719]"
                      style={{
                        backgroundColor: tag.color,
                      }}
                    >
                      {tag.name}
                    </Badge>
                  </DropdownMenuItem>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
